'use client';

import { useCallback, useState } from 'react';

import { stateChartReveal, type ChartRevealPhase } from '@/config/state-chart-reveal';
import {
  STATE_CHART_DEFAULT_VIEW_MODE,
  type StateChartViewMode,
} from '@/constants/stateCharts';
import { useInView } from '@/hooks/useInView';

type UseChartRevealOptions = {
  viewMode: StateChartViewMode;
  reducedMotion: boolean;
};

export type UseChartRevealResult = {
  /** Attach to a stable plate wrapper (survives donut ↔ bar swaps). */
  ref: React.RefObject<HTMLDivElement | null>;
  inView: boolean;
  /** View mode currently rendered — lags `viewMode` until exit completes. */
  displayViewMode: StateChartViewMode;
  phase: ChartRevealPhase;
  onExitComplete: () => void;
  onEnterComplete: () => void;
};

type RevealInputs = {
  inView: boolean;
  viewMode: StateChartViewMode;
  reducedMotion: boolean;
};

/**
 * Scroll-into-view entrance + Species/Habitats exit→enter orchestration.
 *
 * `displayViewMode` stays on the outgoing chart until exit finishes, then
 * updates and `phase` becomes `enter` for the incoming chart.
 *
 * Input changes are folded into phase/display during render (React’s
 * “adjusting state when props change” pattern) so we never setState inside
 * an effect for this orchestration.
 */
export function useChartReveal({
  viewMode,
  reducedMotion,
}: UseChartRevealOptions): UseChartRevealResult {
  const { ref, inView } = useInView<HTMLDivElement>({
    once: stateChartReveal.inViewOnce,
    margin: stateChartReveal.inViewMargin,
    threshold: stateChartReveal.inViewThreshold,
  });

  const [displayViewMode, setDisplayViewMode] = useState<StateChartViewMode>(
    viewMode ?? STATE_CHART_DEFAULT_VIEW_MODE,
  );
  const [phase, setPhase] = useState<ChartRevealPhase>('hidden');
  const [pendingViewMode, setPendingViewMode] =
    useState<StateChartViewMode | null>(null);

  const [fold, setFold] = useState<RevealInputs>({
    inView,
    viewMode,
    reducedMotion,
  });

  if (
    fold.inView !== inView ||
    fold.viewMode !== viewMode ||
    fold.reducedMotion !== reducedMotion
  ) {
    setFold({ inView, viewMode, reducedMotion });

    if (reducedMotion) {
      setDisplayViewMode(viewMode);
      setPendingViewMode(null);
      if (inView) setPhase('shown');
    } else if (phase === 'hidden') {
      // Not yet revealed — keep display in sync; enter on first in-view.
      setDisplayViewMode(viewMode);
      setPendingViewMode(null);
      if (inView) setPhase('enter');
    } else if (viewMode !== displayViewMode) {
      setPendingViewMode(viewMode);
      if (phase !== 'exit') setPhase('exit');
    } else {
      setPendingViewMode(null);
    }
  }

  const onExitComplete = useCallback(() => {
    const next = pendingViewMode ?? viewMode;
    setPendingViewMode(null);
    setDisplayViewMode(next);
    setPhase(reducedMotion ? 'shown' : 'enter');
  }, [pendingViewMode, viewMode, reducedMotion]);

  const onEnterComplete = useCallback(() => {
    setPhase('shown');
  }, []);

  return {
    ref,
    inView,
    displayViewMode,
    phase,
    onExitComplete,
    onEnterComplete,
  };
}
